import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Box, CircularProgress, Typography, useTheme } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import PostDetail from "../components/PostDetail";
import { fetchPost } from "../services/api";
import blogTheme from "../theme/blogTheme";

/**
 * Renders a "Back to Blog" anchor using React Router Link.
 * Shown in all states (loading, error, 404, success) so the user always has
 * an escape hatch.
 */
const BackLink = ({ theme }) => (
  <Box sx={{ mb: 3 }}>
    <Box
      component={Link}
      to="/blog"
      sx={{
        fontFamily: "'Fira Code', monospace",
        fontSize: "0.875rem",
        color: theme.palette.primary.main,
        textDecoration: "none",
        "&:hover": { textDecoration: "underline" },
      }}
    >
      {"← Back to Blog"}
    </Box>
  </Box>
);

/**
 * Page component for an individual blog post.
 *
 * - Extracts `slug` from the URL via useParams()
 * - Fetches the post from the API; handles loading, 404, and generic errors
 * - Wraps the content zone in blogTheme (same nested ThemeProvider pattern as
 *   BlogProjects.js) so PostDetail picks up the correct typography overrides
 */
const BlogPost = () => {
  const { slug } = useParams();
  const theme = useTheme();

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError(null);
    setNotFound(false);
    setPost(null);

    fetchPost(slug)
      .then((data) => {
        if (!cancelled) {
          setPost(data);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          if (err.message && err.message.includes("404")) {
            setNotFound(true);
          } else {
            setError(err.message || "An unexpected error occurred.");
          }
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <Box p={{ xs: 1, sm: 2 }}>
        <BackLink theme={theme} />
        <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
          <CircularProgress size={32} sx={{ color: theme.palette.primary.main }} />
        </Box>
      </Box>
    );
  }

  if (notFound) {
    return (
      <Box p={{ xs: 1, sm: 2 }}>
        <BackLink theme={theme} />
        <Typography
          sx={{
            fontFamily: "'Fira Code', monospace",
            color: theme.palette.comment.main,
            mt: 2,
          }}
        >
          {"// 404 — Post not found"}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={{ xs: 1, sm: 2 }}>
        <BackLink theme={theme} />
        <Typography
          sx={{
            fontFamily: "'Fira Code', monospace",
            color: theme.palette.error.main,
            mt: 2,
          }}
        >
          {`// Error: ${error}`}
        </Typography>
      </Box>
    );
  }

  return (
    <Box p={{ xs: 1, sm: 2 }}>
      <BackLink theme={theme} />

      {/* Scope blogTheme to the article zone only — mirrors BlogProjects.js */}
      <ThemeProvider theme={blogTheme}>
        <Box
          sx={{
            p: { xs: 1.5, sm: 2.5 },
            backgroundColor: "background.default",
            borderRadius: 2,
          }}
        >
          <PostDetail post={post} />
        </Box>
      </ThemeProvider>
    </Box>
  );
};

export default BlogPost;
